import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { reduxForm, FieldArray, formValueSelector } from 'redux-form';
import Dropdown from 'components/Dropdown';
import { GET, POST } from 'utils/httpMethods';
import VersionPanel from './VersionPanel';

import './DcaSettings.scss';

const statusLabels = {
    draft: 'brouillon',
    active: 'active',
    archived: 'archivée',
};

const renderDcaDropdown = ({ fields, type, form, activeVersionId, onChanged, setMessage }) => (
    <div>
        {fields.map((name, index) => {
            const version = fields.get(index);
            const isActive = version.id === activeVersionId;

            return (
                <Dropdown
                    key={version.id}
                    active={isActive}
                    title={`${version.label} — ${statusLabels[version.status] || version.status}`}
                >
                    <VersionPanel
                        form={form}
                        type={type}
                        name={name}
                        version={version}
                        isActive={isActive}
                        onChanged={onChanged}
                        setMessage={setMessage}
                    />
                </Dropdown>
            );
        })}
    </div>
);

renderDcaDropdown.propTypes = {
    fields: PropTypes.shape({
        map: PropTypes.func.isRequired,
        get: PropTypes.func.isRequired,
    }).isRequired,
    type: PropTypes.oneOf(['lifeInsurance', 'per']).isRequired,
    form: PropTypes.string.isRequired,
    activeVersionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onChanged: PropTypes.func.isRequired,
    setMessage: PropTypes.func.isRequired,
};

renderDcaDropdown.defaultProps = {
    activeVersionId: null,
};

// Le mode de gestion du DCA pour un produit:
// upload d'une nouvelle version et activation.
// Formulaire redux-form indépendant du formulaire général de paramètres de simulation (settings-simulation-edit),
// justement pour ne jamais transporter de snapshot périmé des versions DCA lors de son enregistrement.
const DcaFormInner = ({ type, form, activeVersionId, initialize }) => {
    const [message, setMessage] = useState('');

    const load = useCallback(async () => {
        const response = await GET(`/api/dca/${type}`);
        const data = await response.json();
        initialize({ versions: data.versions, activeVersionId: data.activeVersionId });
    }, [type, initialize]);

    useEffect(() => {
        load();
    }, [load]);

    const uploadDca = async (event) => {
        const [file] = event.target.files;
        // eslint-disable-next-line no-param-reassign
        event.target.value = '';
        if (!file) {
            return;
        }

        try {
            const formData = new FormData();
            formData.append('dca', file);
            await POST(`/api/dca/${type}/uploadDca`, { body: formData });
            setMessage('Nouvelle version ajoutée en brouillon.');
            await load();
        } catch (error) {
            setMessage('Le téléchargement a échoué : le fichier doit être un PDF.');
        }
    };

    return (
        <div className="dca-settings">
            {message && <p className="dca-settings__message">{message}</p>}
            <label className="dca-settings__upload" htmlFor={`dca-upload-${type}`}>
                Télécharger une nouvelle version
                <input id={`dca-upload-${type}`} type="file" accept="application/pdf" onChange={uploadDca} />
            </label>
            <FieldArray
                name="versions"
                component={renderDcaDropdown}
                type={type}
                form={form}
                activeVersionId={activeVersionId}
                onChanged={load}
                setMessage={setMessage}
            />
        </div>
    );
};

DcaFormInner.propTypes = {
    type: PropTypes.oneOf(['lifeInsurance', 'per']).isRequired,
    form: PropTypes.string.isRequired,
    activeVersionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    initialize: PropTypes.func.isRequired,
};

DcaFormInner.defaultProps = {
    activeVersionId: null,
};

// redux-form accepte le nom du form en prop (au lieu de la config statique) : on peut donc
// réutiliser le même composant pour les deux types (lifeInsurance, per) sans le recréer à chaque rendu.
const Form = connect((state, { form }) => ({
    activeVersionId: formValueSelector(form)(state, 'activeVersionId'),
}))(reduxForm({ enableReinitialize: true })(DcaFormInner));

const DcaSettings = ({ type }) => <Form type={type} form={`dca-settings-${type}`} />;

DcaSettings.propTypes = {
    type: PropTypes.oneOf(['lifeInsurance', 'per']).isRequired,
};

export default DcaSettings;
